import { useState, useRef } from 'react'
import { preview as platformPreview } from '@carrent/platform'
import { api } from '@carrent/api'
import { dialog, geo } from '@carrent/platform'
import DamageStep from './DamageStep'
import { enqueueFinishTrip, flushNow } from '../../offline/tripTasks'

const PHOTO_STEPS = [
  { key: 'front',    label: 'Спереди',    hint: 'Передний бампер, капот, фары',          emoji: '⬆️', required: true  },
  { key: 'back',     label: 'Сзади',      hint: 'Задний бампер, номерной знак, фонари',  emoji: '⬇️', required: true  },
  { key: 'left',     label: 'Левый бок',  hint: 'Левая сторона кузова целиком',          emoji: '⬅️', required: false },
  { key: 'right',    label: 'Правый бок', hint: 'Правая сторона кузова целиком',         emoji: '➡️', required: false },
  { key: 'interior', label: 'Салон',      hint: 'Общий вид салона, сиденья, коврики',    emoji: '🪑', required: false },
  { key: 'odometer', label: 'Одометр',    hint: 'Приборная панель — конечный пробег',    emoji: '🔢', required: true  },
]

const REQUIRED_KEYS = PHOTO_STEPS.filter(s => s.required).map(s => s.key)

function PhotoStep({ step, photo, onPhoto }) {
  // Превью из пропа — при навигации назад/вперёд фото сохраняется
  const inputRef = useRef()
  const preview = photo ? platformPreview.objectUrl(photo) : null

  const handleFile = (e) => {
    const file = e.target.files[0]
    if (!file) return
    onPhoto(file)
    e.target.value = ''
  }

  const handleRetake = () => {
    onPhoto(null)
    setTimeout(() => inputRef.current?.click(), 50)
  }

  return (
    <div className="flex-col" style={{ gap: 'var(--gap)', flex: 1 }}>
      <div style={{
        background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
        borderRadius: 'var(--r)', padding: 'var(--gap)',
      }}>
        <div style={{ fontSize: '2.5rem', textAlign: 'center', marginBottom: 8 }}>{step.emoji}</div>
        <p style={{ fontSize: '0.88rem', color: 'var(--warning)', textAlign: 'center', lineHeight: 1.6 }}>
          {step.hint}
        </p>
        {step.required && (
          <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--danger)', marginTop: 4 }}>
            * Обязательное фото
          </p>
        )}
      </div>

      {preview ? (
        <div style={{ position: 'relative' }}>
          <img src={preview} alt={step.label}
            style={{ width: '100%', borderRadius: 'var(--r)', maxHeight: 280, objectFit: 'cover',
              border: '2px solid var(--success)' }} />
          <button onClick={handleRetake}
            style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.7)',
              color: '#fff', border: 'none', borderRadius: 'var(--r-sm)',
              padding: '6px 12px', cursor: 'pointer', fontSize: '0.82rem' }}>
            🔄 Переснять
          </button>
          <div style={{ position: 'absolute', bottom: 8, left: 8,
            background: 'rgba(34,197,94,0.9)', color: '#fff',
            padding: '4px 12px', borderRadius: 99, fontSize: '0.78rem', fontWeight: 700 }}>
            ✓ Фото сделано
          </div>
        </div>
      ) : (
        <button onClick={() => inputRef.current?.click()} style={{
          height: 220, border: '2px dashed var(--border)', borderRadius: 'var(--r)',
          background: 'var(--bg-card)', cursor: 'pointer', display: 'flex',
          flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 12, color: 'var(--text-muted)',
        }}>
          <span style={{ fontSize: '3rem' }}>📷</span>
          <span style={{ fontSize: '0.9rem' }}>Нажмите для съёмки</span>
        </button>
      )}

      <input ref={inputRef} type="file" accept="image/*" capture="environment"
        style={{ display: 'none' }} onChange={handleFile} />
    </div>
  )
}


// ── Дополнительные фото ───────────────────────────────────────────────────────
function ExtraPhotos({ extras, onAdd, onRemove }) {
  const inputRef = useRef()

  const handleFile = (e) => {
    Array.from(e.target.files).forEach(f => onAdd(f))
    e.target.value = ''
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label className="field__label" style={{ margin: 0 }}>
          Дополнительные фото
          <span className="text-muted" style={{ fontWeight: 400, marginLeft: 6, fontSize: '0.75rem' }}>
            (повреждения, сколы, царапины)
          </span>
        </label>
        <button type="button" onClick={() => inputRef.current?.click()}
          className="btn btn--outline"
          style={{ padding: '4px 12px', fontSize: '0.8rem', flexShrink: 0 }}>
          + Добавить
        </button>
      </div>

      {extras.length === 0 ? (
        <button type="button" onClick={() => inputRef.current?.click()} style={{
          width: '100%', height: 72, border: '1px dashed var(--border)',
          borderRadius: 'var(--r)', background: 'transparent', cursor: 'pointer',
          color: 'var(--text-muted)', fontSize: '0.85rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <span style={{ fontSize: '1.3rem' }}>📎</span>
          Добавьте фото повреждений
        </button>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
          {extras.map(ex => (
            <div key={ex.id} style={{ position: 'relative', aspectRatio: '4/3' }}>
              <img src={ex.preview} alt="доп. фото"
                style={{ width: '100%', height: '100%', objectFit: 'cover',
                  borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' }} />
              <button type="button" onClick={() => onRemove(ex.id)} style={{
                position: 'absolute', top: 3, right: 3,
                width: 22, height: 22, borderRadius: '50%',
                background: 'rgba(239,68,68,0.85)', color: '#fff',
                border: 'none', cursor: 'pointer', fontSize: '0.75rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>✕</button>
            </div>
          ))}
          <button type="button" onClick={() => inputRef.current?.click()} style={{
            aspectRatio: '4/3', border: '1px dashed var(--border)',
            borderRadius: 'var(--r-sm)', background: 'transparent', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-muted)', fontSize: '1.5rem',
          }}>+</button>
        </div>
      )}

      <input ref={inputRef} type="file" accept="image/*" capture="environment"
        multiple style={{ display: 'none' }} onChange={handleFile} />
    </div>
  )
}

export default function FinishTripWizard({ trip, car, onCancel, onFinished }) {
  const [step, setStep]         = useState(0)
  const [photos, setPhotos]     = useState({})
  const [endMileage, setEnd]    = useState(trip.start_mileage?.toString() || '')
  const [hasDamage, setDamage]  = useState(false)
  const [comment, setComment]   = useState('')
  const [loading, setLoading]   = useState(false)
  const [err, setErr]           = useState('')
  const [extras, setExtras]     = useState([])
  const [damageMarks, setDamageMarks] = useState([])
  const [queued, setQueued] = useState(false)
  const [draftId] = useState(() =>
    globalThis.crypto?.randomUUID?.().replace(/[^a-zA-Z0-9-]/g, '')
    ?? `d-${Date.now()}-${Math.random().toString(36).slice(2)}`)
  const addExtra    = (file) => setExtras(ex => [...ex, { id: Date.now(), file, preview: platformPreview.objectUrl(file) }])
  const removeExtra = (id)  => setExtras(ex => ex.filter(e => e.id !== id))

  const totalPhotoSteps = PHOTO_STEPS.length
  const isPhotoStep     = step < totalPhotoSteps
  const isDamageStep    = step === totalPhotoSteps
  const isDataStep      = step === totalPhotoSteps + 1
  const isConfirmStep   = step === totalPhotoSteps + 2
  const totalSteps      = totalPhotoSteps + 3

  const currentPhotoStep = PHOTO_STEPS[step]
  const setPhoto = (key, file) => setPhotos(p => ({ ...p, [key]: file }))

  const canGoNext = () => {
    if (isPhotoStep) {
      const s = currentPhotoStep
      return !s.required || !!photos[s.key]
    }
    if (isDataStep) return endMileage.trim() !== ''
    return true
  }

  const submit = async () => {
    // Критичное повреждение на сдаче — машина не должна уйти
    // следующему водителю без осмотра.
    const critical = damageMarks.filter(m => m.severity === 'critical')
    if (critical.length) {
      const proceed = await dialog.confirm(
        `Отмечено критичных повреждений: ${critical.length}. ` +
        'Машина уйдёт на осмотр. Завершить поездку?')
      if (!proceed) return
    }

    setLoading(true); setErr('')

    try {
      await enqueueFinishTrip({
        draftId,
        tripId: trip.id,
        endMileage,
        hasDamage,
        comment,
        photoMap: photos,
        extras,
        marks: damageMarks,
        // Машину чаще всего сдают там же, где брали, — в паркинге.
        // Сигнала может не быть, и это нормально: сдача не блокируется.
        geo: await geo.getPositionSmart(),
      })
    } catch {
      setErr('Не удалось сохранить акт на устройстве. Освободите место и попробуйте снова.')
      setLoading(false)
      return
    }

    try {
      await flushNow()
      // Поездка завершена, если сервер больше не считает её активной.
      const { data } = await api.get('/trips/active')
      if (!data?.data) {
        onFinished(null)
        return
      }
    } catch {
      // Сети нет — акт остался в очереди.
    }

    setLoading(false)
    setQueued(true)
  }

  const distance = parseInt(endMileage || 0) - (trip.start_mileage || 0)
  const progressPct = Math.round((step / (totalSteps - 1)) * 100)

  if (queued) {
    return (
      <div className="queued-screen">
        <h2 className="queued-screen__title">Акт сохранён</h2>
        <p className="queued-screen__text">
          Сети сейчас нет. Фотографии и отметки повреждений лежат
          на устройстве и уйдут сами, как только связь появится.
        </p>
        <p className="queued-screen__hint">
          Поездка закроется на сервере после отправки. Следить можно
          в разделе «Ещё» → «Ожидают отправки».
        </p>
        <button className="btn btn--primary btn--full" onClick={onCancel}>
          Понятно
        </button>
      </div>
    )
  }


  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      background: 'var(--bg)', paddingBottom: 'env(safe-area-inset-bottom, 20px)' }}>

      {/* Шапка */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-sm)',
        padding: 'var(--gap)', paddingTop: 'calc(var(--gap) + env(safe-area-inset-top, 0px))',
        borderBottom: '1px solid var(--border)', background: 'rgba(239,68,68,0.05)' }}>
        <button onClick={onCancel}
          style={{ background: 'none', border: 'none', color: 'var(--text)',
            fontSize: '1.5rem', cursor: 'pointer', padding: 4 }}>‹</button>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>Завершение поездки</p>
          <p style={{ fontWeight: 600, fontSize: '0.95rem' }}>
            {car?.brand} {car?.model} · {car?.plate}
          </p>
        </div>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{step + 1}/{totalSteps}</span>
      </div>

      {/* Прогресс */}
      <div style={{ height: 3, background: 'var(--border)' }}>
        <div style={{ height: '100%', background: 'var(--danger)',
          width: `${progressPct}%`, transition: 'width 0.3s' }} />
      </div>

      {/* Индикаторы фото */}
      <div style={{ display: 'flex', gap: 4, padding: '8px var(--gap)' }}>
        {PHOTO_STEPS.map((s, i) => (
          <div key={s.key} style={{
            flex: 1, height: 4, borderRadius: 99,
            background: photos[s.key] ? 'var(--success)'
              : i === step ? 'var(--danger)' : 'var(--border)',
            transition: 'background 0.2s',
            outline: s.required && !photos[s.key] && i !== step ? '1px solid rgba(239,68,68,0.4)' : 'none',
          }} />
        ))}
      </div>

      {/* Контент */}
      <div style={{ flex: 1, padding: '0 var(--gap)', display: 'flex', flexDirection: 'column' }}>

        {isPhotoStep && (
          <>
            <h2 style={{ fontSize: '1.3rem', marginBottom: 'var(--gap-sm)', marginTop: 'var(--gap-sm)' }}>
              {currentPhotoStep.label}
              {!currentPhotoStep.required && (
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)',
                  fontWeight: 400, marginLeft: 8 }}>необязательно</span>
              )}
            </h2>
            <PhotoStep step={currentPhotoStep}
              photo={photos[currentPhotoStep.key]}
              onPhoto={(f) => setPhoto(currentPhotoStep.key, f)} />
          </>
        )}

        {isDamageStep && (
          <>
            <h2 className="damage-step__title">Повреждения</h2>
            <p className="damage-step__lead">
              Отметьте всё, что появилось за поездку. Прежние повреждения
              уже на схеме — их отмечать не нужно.
            </p>
            <DamageStep car={trip.car || car} marks={damageMarks}
              onChange={setDamageMarks} />
          </>
        )}

        {isDataStep && (
          <div className="flex-col" style={{ gap: 'var(--gap)', paddingTop: 'var(--gap-sm)' }}>
            <h2 style={{ fontSize: '1.3rem' }}>Итоги поездки</h2>

            <div className="field">
              <label className="field__label">
                Конечный пробег (км) <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input className="input" type="number" inputMode="numeric"
                style={{ fontSize: '1.4rem', fontWeight: 700, textAlign: 'center', height: 64 }}
                value={endMileage} onChange={e => setEnd(e.target.value)}
                min={trip.start_mileage} />
              {endMileage && parseInt(endMileage) > trip.start_mileage && (
                <p style={{ fontSize: '0.82rem', color: 'var(--success)', marginTop: 4 }}>
                  Пройдено: {(parseInt(endMileage) - trip.start_mileage).toLocaleString()} км
                </p>
              )}
            </div>

            {/* Флаг повреждений */}
            <button onClick={() => setDamage(d => !d)} style={{
              padding: 'var(--gap)', border: `2px solid ${hasDamage ? 'var(--danger)' : 'var(--border)'}`,
              borderRadius: 'var(--r)', background: hasDamage ? 'rgba(239,68,68,0.08)' : 'var(--bg-card)',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 'var(--gap)',
              transition: 'all 0.15s', textAlign: 'left',
            }}>
              <div style={{
                width: 24, height: 24, borderRadius: 6, border: `2px solid ${hasDamage ? 'var(--danger)' : 'var(--border)'}`,
                background: hasDamage ? 'var(--danger)' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'all 0.15s',
              }}>
                {hasDamage && <span style={{ color: '#fff', fontSize: '0.9rem' }}>✓</span>}
              </div>
              <div>
                <p style={{ fontWeight: 600, color: hasDamage ? 'var(--danger)' : 'var(--text)' }}>
                  ⚠️ Есть повреждения
                </p>
                <p className="text-muted" style={{ fontSize: '0.78rem', marginTop: 2 }}>
                  Машина будет отправлена на осмотр
                </p>
              </div>
            </button>

            <div className="field">
              <label className="field__label">Комментарий по поездке</label>
              <textarea className="input" rows={3}
                placeholder={hasDamage ? 'Опишите повреждения подробно...' : 'Дополнительные замечания...'}
                value={comment} onChange={e => setComment(e.target.value)}
                style={{ resize: 'vertical', borderColor: hasDamage ? 'rgba(239,68,68,0.4)' : '' }} />
            </div>

            <ExtraPhotos extras={extras} onAdd={addExtra} onRemove={removeExtra} />
          </div>
        )}

        {isConfirmStep && (
          <div className="flex-col" style={{ gap: 'var(--gap)', paddingTop: 'var(--gap-sm)' }}>
            <h2 style={{ fontSize: '1.3rem' }}>Подтвердите завершение</h2>

            {/* Миниатюры фото */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
              {PHOTO_STEPS.map(s => photos[s.key] ? (
                <div key={s.key} style={{ position: 'relative', aspectRatio: '4/3' }}>
                  <img src={platformPreview.objectUrl(photos[s.key])} alt={s.label}
                    style={{ width: '100%', height: '100%', objectFit: 'cover',
                      borderRadius: 'var(--r-sm)' }} />
                  <div style={{ position: 'absolute', bottom: 2, left: 2, right: 2,
                    background: 'rgba(0,0,0,0.6)', borderRadius: 4,
                    padding: '1px 4px', fontSize: '0.62rem', color: '#fff', textAlign: 'center' }}>
                    {s.label}
                  </div>
                </div>
              ) : (
                <div key={s.key} style={{ aspectRatio: '4/3', background: 'var(--bg-card)',
                  borderRadius: 'var(--r-sm)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '0.62rem', color: 'var(--text-muted)',
                  border: '1px solid var(--border)' }}>
                  {s.label}<br/>—
                </div>
              ))}
              {extras.map((ex, i) => (
                <div key={ex.id} style={{ position: 'relative', aspectRatio: '4/3' }}>
                  <img src={ex.preview} alt={`доп ${i+1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover',
                      borderRadius: 'var(--r-sm)', border: '1px solid rgba(245,158,11,0.5)' }} />
                  <div style={{ position: 'absolute', bottom: 2, left: 2, right: 2,
                    background: 'rgba(0,0,0,0.6)', borderRadius: 4,
                    padding: '1px 4px', fontSize: '0.62rem', color: '#fff', textAlign: 'center' }}>
                    Доп. {i+1}
                  </div>
                </div>
              ))}
            </div>

            {/* Итоги */}
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {[
                ['Начальный пробег', `${trip.start_mileage?.toLocaleString()} км`],
                ['Конечный пробег',  `${parseInt(endMileage).toLocaleString()} км`],
                ['Пройдено',         `${distance.toLocaleString()} км`],
              ].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between',
                  padding: '10px var(--gap)', borderBottom: '1px solid var(--border)' }}>
                  <span className="text-muted" style={{ fontSize: '0.85rem' }}>{l}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>

            {hasDamage && (
              <div className="alert alert--error">
                ⚠️ Зафиксированы повреждения — машина будет отправлена на осмотр
              </div>
            )}
            {err && <div className="alert alert--error">{err}</div>}
          </div>
        )}
      </div>

      {/* Навигация */}
      <div style={{ padding: 'var(--gap)', paddingTop: 'var(--gap-sm)',
        borderTop: '1px solid var(--border)', display: 'flex', gap: 'var(--gap-sm)' }}>
        {step > 0 && !isConfirmStep && (
          <button className="btn btn--outline" style={{ flex: 1 }}
            onClick={() => setStep(s => s - 1)}>← Назад</button>
        )}
        {!isConfirmStep ? (
          <button className="btn" style={{
            flex: 2, background: 'var(--danger)', color: '#fff',
            border: 'none', borderRadius: 'var(--r)', height: 48, cursor: 'pointer',
            opacity: canGoNext() ? 1 : 0.5, fontSize: '0.95rem', fontWeight: 600,
          }} onClick={() => canGoNext() && setStep(s => s + 1)}
            disabled={!canGoNext()}>
            {isDataStep ? 'Проверить →' : 'Далее →'}
          </button>
        ) : (
          <>
            <button className="btn btn--outline" style={{ flex: 1 }}
              onClick={() => setStep(s => s - 1)}>← Назад</button>
            <button style={{
              flex: 2, height: 52, background: 'var(--danger)', color: '#fff',
              border: 'none', borderRadius: 'var(--r)', cursor: 'pointer',
              fontSize: '1rem', fontWeight: 700,
            }} onClick={submit} disabled={loading}>
              {loading ? <span className="spinner" /> : '🏁 Завершить поездку'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
